/** Configure file selection and drag-and-drop uploads. */
import { splitKey } from "./paths.js";

/**
 * @typedef {{
 *   fileContainerElement: HTMLParagraphElement;
 *   fileInputElement: HTMLInputElement;
 *   secret: any;
 *   api: any;
 *   listController: any;
 *   queueUpload: any;
 *   fileToListPrefixMap: WeakMap<File, string>;
 *   localizer: any;
 * }} FileInputControllerOptions
 */

/** File selection and drag-and-drop controller. */
export default class FileInputController {
  /** @param {FileInputControllerOptions} options */
  constructor({
    fileContainerElement,
    fileInputElement,
    secret,
    api,
    listController,
    queueUpload,
    fileToListPrefixMap,
    localizer,
  }) {
    /** @param {readonly FileSystemEntry[]} entries */
    const getFilesFromEntries = async (entries) => {
      /** @type {[File, string][]} */
      const filePrefixesMap = [];
      /** @param {FileSystemEntry} entry @param {string} prefix */
      const scanFiles = async (entry, prefix) => {
        if (entry.isDirectory) {
          const directoryReader = /** @type {FileSystemDirectoryEntry} */ (
            entry
          ).createReader();
          const entries = await new Promise((resolve) =>
            directoryReader.readEntries(resolve, resolve),
          );
          if (!Array.isArray(entries)) return;
          for (const subDirEntry of entries) {
            await scanFiles(subDirEntry, prefix + entry.name + "/");
          }
          return;
        }
        const file = await new Promise((resolve) =>
          /** @type {FileSystemFileEntry} */ (entry).file(resolve, resolve),
        );
        if (file instanceof File) filePrefixesMap.push([file, prefix]);
      };
      for (const entry of entries)
        await scanFiles(entry, listController.listPrefix);
      return filePrefixesMap;
    };

    /** @param {[File, string][]} filePrefixesMap @param {string} eventName */
    const addFiles = async (filePrefixesMap, eventName) => {
      for (const [file, prefix] of filePrefixesMap) {
        fileToListPrefixMap.set(file, prefix);
        _paq.push(["trackEvent", "QueueUpload", "AddQueue", file.name]);
      }
      await listController.addPendingFilesToList(filePrefixesMap);
      _paq.push([
        "trackEvent",
        "QueueUpload",
        "AddQueueLength",
        filePrefixesMap.length.toString(),
      ]);
      _paq.push([
        "trackEvent",
        "UI",
        eventName,
        filePrefixesMap.length.toString(),
      ]);
      queueUpload.addQueue(filePrefixesMap.map(([file]) => file));
    };

    fileContainerElement.addEventListener(
      "drop",
      async (/** @type {DragEvent} */ evt) => {
        evt.preventDefault();
        document.body.classList.remove("dragover");
        if (!secret.getSecret() || !api.configStore.config) {
          alert(localizer.get("loginFirst"));
          return;
        }
        const items = evt.dataTransfer?.items;
        if (!items) return;
        const entries = [];
        for (let i = 0; i < items.length; i++) {
          const entry = items[i].webkitGetAsEntry();
          if (entry) entries.push(entry);
        }
        await addFiles(await getFilesFromEntries(entries), "FileDrop");
      },
    );
    fileContainerElement.addEventListener(
      "dragover",
      (/** @type {DragEvent} */ evt) => evt.preventDefault(),
    );
    fileContainerElement.addEventListener(
      "dragenter",
      (/** @type {DragEvent} */ evt) => {
        document.body.classList.add("dragover");
        evt.preventDefault();
      },
    );
    fileContainerElement.addEventListener(
      "dragleave",
      (/** @type {DragEvent} */ evt) => {
        document.body.classList.remove("dragover");
        evt.preventDefault();
      },
    );
    window.addEventListener("drop", (evt) => evt.preventDefault());
    window.addEventListener("dragover", (evt) => evt.preventDefault());

    fileInputElement.addEventListener(
      "change",
      /** @this {HTMLInputElement} */
      async function () {
        if (!secret.getSecret() || !api.configStore.config) {
          alert(localizer.get("loginFirst"));
          this.form?.reset();
          return;
        }
        if (!this.files) {
          this.form?.reset();
          return;
        }
        /** @type {[File, string][]} */
        const filePrefixesMap = Array.from(this.files).map((file) => {
          const prefix = splitKey(
            listController.listPrefix + (file.webkitRelativePath ?? file.name),
          ).prefix;
          return [file, prefix];
        });
        await addFiles(filePrefixesMap, "FileSelect");
        this.form?.reset();
      },
    );
  }
}
